import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay, endOfDay } from 'date-fns';

const Report = () => {
  const [stats, setStats] = useState({
    income: 0,
    expenses: 0,
    profit: 0,
    totalClients: 0,
    haircutsCount: 0
  });
  const [haircuts, setHaircuts] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [period, setPeriod] = useState('today');
  const [customDates, setCustomDates] = useState({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadReport();
  }, [period, customDates]);

  const getDateRange = () => {
    const now = new Date();
    
    switch (period) {
      case 'today':
        return {
          startDate: startOfDay(now).toISOString(),
          endDate: endOfDay(now).toISOString()
        };
      case 'week':
        return {
          startDate: startOfWeek(now, { weekStartsOn: 1 }).toISOString(),
          endDate: endOfWeek(now, { weekStartsOn: 1 }).toISOString()
        };
      case 'month':
        return {
          startDate: startOfMonth(now).toISOString(),
          endDate: endOfMonth(now).toISOString()
        };
      case 'custom':
        return {
          startDate: new Date(customDates.startDate).toISOString(),
          endDate: new Date(customDates.endDate + 'T23:59:59').toISOString()
        };
      default:
        return {};
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const dateRange = getDateRange();
      
      const [statsRes, haircutsRes, expensesRes] = await Promise.all([
        api.getStats(dateRange),
        api.getHaircuts(dateRange),
        api.getExpenses(dateRange)
      ]);

      setStats(statsRes.data);
      setHaircuts(haircutsRes.data);
      setExpenses(expensesRes.data);
    } catch (error) {
      console.error('Ошибка загрузки отчета:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('uz-UZ').format(num);
  };

  const getPeriodLabel = () => {
    switch (period) {
      case 'today':
        return 'Сегодня';
      case 'week':
        return 'Эта неделя';
      case 'month':
        return 'Этот месяц';
      case 'custom':
        return `${format(new Date(customDates.startDate), 'dd.MM.yyyy')} - ${format(new Date(customDates.endDate), 'dd.MM.yyyy')}`;
      default:
        return '';
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">Отчёт</h1>
        <p className="text-gray-400">{getPeriodLabel()}</p>
      </div>

      {/* Period Selector - Компактная версия */}
      <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap items-center gap-2">
          {[
            { key: 'today', label: 'Сегодня' },
            { key: 'week', label: 'Неделя' },
            { key: 'month', label: 'Месяц' },
            { key: 'custom', label: 'Свой период' }
          ].map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                period === p.key
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                  : 'bg-gray-700/30 text-gray-400 border border-gray-600/50 hover:border-gray-500'
              }`}
            >
              {p.label}
            </button>
          ))}

          {period === 'custom' && (
            <>
              <input
                type="date"
                value={customDates.startDate}
                onChange={(e) => setCustomDates({...customDates, startDate: e.target.value})}
                className="px-3 py-2 bg-gray-700/50 border border-gray-600/50 rounded-lg text-gray-300 text-sm focus:outline-none focus:border-gray-500"
              />
              <span className="text-gray-500">—</span>
              <input
                type="date"
                value={customDates.endDate}
                onChange={(e) => setCustomDates({...customDates, endDate: e.target.value})}
                className="px-3 py-2 bg-gray-700/50 border border-gray-600/50 rounded-lg text-gray-300 text-sm focus:outline-none focus:border-gray-500"
              />
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-500"></div>
        </div>
      ) : (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gray-800/50 backdrop-blur-sm border border-emerald-500/50 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-medium text-emerald-400">Доход</h3>
                <div className="w-10 h-10 bg-emerald-500/10 border border-emerald-500/50 rounded-lg flex items-center justify-center">
                  <span className="text-emerald-400 font-bold">$</span>
                </div>
              </div>
              <p className="text-3xl font-bold text-white">{formatNumber(stats.income)} сўм</p>
              <p className="text-sm text-gray-400 mt-2">Всего стрижек: {stats.haircutsCount}</p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm border border-rose-500/50 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-medium text-rose-400">Расходы</h3>
                <div className="w-10 h-10 bg-rose-500/10 border border-rose-500/50 rounded-lg flex items-center justify-center">
                  <span className="text-rose-400 font-bold">$</span>
                </div>
              </div>
              <p className="text-3xl font-bold text-white">{formatNumber(stats.expenses)} сўм</p>
              <p className="text-sm text-gray-400 mt-2">Всего расходов: {expenses.length}</p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm border border-blue-500/50 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-medium text-blue-400">Прибыль</h3>
                <div className="w-10 h-10 bg-blue-500/10 border border-blue-500/50 rounded-lg flex items-center justify-center">
                  <span className="text-blue-400 font-bold">$</span>
                </div>
              </div>
              <p className="text-3xl font-bold text-white">{formatNumber(stats.profit)} сўм</p>
              <p className="text-sm text-gray-400 mt-2">
                {stats.profit >= 0 ? 'Положительная' : 'Отрицательная'}
              </p>
            </div>
          </div>

          {/* Detailed Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Haircuts Detail */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-emerald-500/30">
                <h2 className="text-xl font-bold text-emerald-400">
                  Детали стрижек
                </h2>
              </div>
              <div className="p-6">
                {haircuts.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Нет данных за выбранный период</p>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {haircuts.map((haircut) => (
                      <div key={haircut._id} className="border-l-4 border-emerald-500 bg-gray-700/30 p-4 rounded-r-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold text-white">{haircut.client?.name}</p>
                            <p className="text-sm text-gray-400">{haircut.type}</p>
                          </div>
                          <span className="font-bold text-emerald-400">{formatNumber(haircut.amount)} сўм</span>
                        </div>
                        <div className="flex justify-between items-center text-xs text-gray-500">
                          <span>{format(new Date(haircut.date), 'dd.MM.yyyy HH:mm')}</span>
                          <span>{haircut.master?.username}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Expenses Detail */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-rose-500/30">
                <h2 className="text-xl font-bold text-rose-400">
                  Детали расходов
                </h2>
              </div>
              <div className="p-6">
                {expenses.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Нет данных за выбранный период</p>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {expenses.map((expense) => (
                      <div key={expense._id} className="border-l-4 border-rose-500 bg-gray-700/30 p-4 rounded-r-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold text-white">{expense.description}</p>
                            <span className="inline-block mt-1 px-2 py-1 bg-gray-700/50 border border-gray-600/50 text-gray-300 text-xs rounded-full">
                              {expense.category}
                            </span>
                          </div>
                          <span className="font-bold text-rose-400">{formatNumber(expense.amount)} сўм</span>
                        </div>
                        <p className="text-xs text-gray-500">{format(new Date(expense.date), 'dd.MM.yyyy HH:mm')}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Report;